/**
 * Microsoft Clarity — per-organization behaviour tracking.
 *
 * A single build serves every organization; the org is identified by the
 * hostname of the customer-portal URL (the same rule AuthProvider.getDomain
 * uses). Each org therefore gets its own Clarity project so one client's
 * session recordings never land in another client's dashboard.
 *
 * To onboard an org: create a Clarity project, then add
 * "<their-portal-host>": "<clarity project id>" to CLARITY_PROJECTS below.
 * An org that isn't listed is simply not tracked — nothing is loaded.
 */

const CLARITY_PROJECTS = {
  // Production — one Clarity project per organization.
  "pay.key2recovery.com": "yk4jyisykv", // Key2
  "p.eleos-si.com": "yls2yoboxy", // Eleos
  "pay.municipalpartner.com": "yls3ne2cuc", // CredStar
  "pay.rvncustomercare.com": "yls449n4cn", // RevNow
  "pay.wmcllc.org": "yls54hfo6x", // WMC

  // Non-production — local, dev, QA and the ngrok tunnel all share the
  // DevTesting project so test traffic never lands in a client's dashboard.
  // The port is irrelevant here: window.location.hostname excludes it, so
  // localhost:3001 matches the "localhost" entry.
  "mxfy.io": "yls7fneny7",
  "dev-rls.mxfy.io": "yls7fneny7",
  "qa.mxfy.io": "yls7fneny7",
  localhost: "yls7fneny7",
  "127.0.0.1": "yls7fneny7",
  "spoiled-asparagus-activate.ngrok-free.dev": "yls7fneny7",
};

// Matching is exact (minus a leading "www."), so a host that isn't listed
// above is never tracked rather than being guessed into the wrong project.
const normalizeHost = (host) => String(host).toLowerCase().replace(/^www\./, "");

export const getClarityProjectId = (host = window.location.hostname) =>
  CLARITY_PROJECTS[normalizeHost(host)] || null;

let isInitialized = false;

/**
 * Loads the Clarity script for the org owning the current hostname.
 * Safe to call more than once; only the first call does anything.
 */
export const initClarity = () => {
  if (isInitialized || typeof window === "undefined") {
    return;
  }

  const projectId = getClarityProjectId();

  if (!projectId) {
    return;
  }

  isInitialized = true;

  // Official Clarity snippet — queues calls made before the script loads.
  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", projectId);

  // Always tag the host so a project still tells you which portal it came
  // from — useful if two orgs ever share a project.
  claritySet("portal_host", window.location.hostname);
};

/** Attaches a filterable key/value tag to the current Clarity session. */
export const claritySet = (key, value) => {
  if (typeof window === "undefined" || !window.clarity || value == null) {
    return;
  }

  try {
    window.clarity("set", key, String(value));
  } catch (error) {
    // Analytics must never break the portal.
  }
};

/** Records a named custom event, filterable alongside Clarity smart events. */
export const clarityEvent = (name) => {
  if (typeof window === "undefined" || !window.clarity || !name) {
    return;
  }

  try {
    window.clarity("event", name);
  } catch (error) {
    // Analytics must never break the portal.
  }
};

const firedOnce = new Set();

/**
 * Records an event at most once per browser-tab session.
 *
 * Needed for impression-style events: ChatBot lives inside the per-route
 * Layout, so it remounts on every navigation. Without this, "was it seen"
 * would count page views instead of visitors and the click-through rate
 * would be meaningless.
 */
export const clarityEventOnce = (name) => {
  if (!name || firedOnce.has(name)) {
    return;
  }

  firedOnce.add(name);

  try {
    const key = `clarity_once_${name}`;

    if (window.sessionStorage.getItem(key)) {
      return;
    }

    window.sessionStorage.setItem(key, "1");
  } catch (error) {
    // Private mode / blocked storage: the in-memory Set still dedupes.
  }

  clarityEvent(name);
};

/**
 * Flags the session as worth keeping when Clarity samples recordings.
 * Use it for the interactions you actually want to watch back.
 */
export const clarityUpgrade = (reason) => {
  if (typeof window === "undefined" || !window.clarity || !reason) {
    return;
  }

  try {
    window.clarity("upgrade", reason);
  } catch (error) {
    // Analytics must never break the portal.
  }
};

// The only routes not prefixed with a customer hash (see CombineRouter).
const TOP_LEVEL_ROUTES = ["invalied"];

// Trailing route params such as /payment-verified/:paymentId are as unique as
// the hash itself, so they get collapsed too.
const looksLikeId = (segment) =>
  /^[0-9a-f]{24}$/i.test(segment) ||
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    segment
  ) ||
  (segment.length >= 12 && /\d/.test(segment));

/**
 * Portal URLs are /:hash/invoices, /:hash/payments and so on, where the hash
 * is unique per customer. Left as-is, every visitor produces a distinct URL
 * and heatmaps/filters cannot group them. This reduces a path to a shape that
 * repeats across customers, e.g. /payment-verified/:id.
 */
export const getPageType = (pathname = window.location.pathname) => {
  const segments = String(pathname).split("/").filter(Boolean);

  if (!segments.length) {
    return "/home";
  }

  const rest = TOP_LEVEL_ROUTES.includes(segments[0])
    ? segments
    : segments.slice(1);

  if (!rest.length) {
    return "/home";
  }

  return `/${rest.map((s) => (looksLikeId(s) ? ":id" : s)).join("/")}`;
};
